import { html, type Html, ListView, on, Page, rune } from 'rune-ts';
import { Title } from '../../components/atoms/Title/Title';
import klass from './PhotoReview.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { PhotoReviewCard } from '../../components/cells/PhotoReviewCard/PhotoReviewCard';
import { classes, isAllCrewById, updateUrlParams } from '../../../shared/util';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { ConfigSentryF } from '../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { PhotoReviewCardType } from '../../../features/PhotoReview/type';
import { typo } from '../../../shared/typography/typo';
import { ButtonAction } from '../../../shared/components/atoms/ButtonAction/ButtonAction';
import { Breadcrumbs } from '../../components/cells/BreadCrumbs/Breadcrumbs';
import { dataStr } from '../../../shared/util/dataStr';
import { makeHomeScreenNavigate, makeUtilScreenNavigate } from '../../../shared/app/navigate';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { SubHeaderMobile } from '../../components/cells/Header/Global/views/SubHeaderMobile.ts';
import { type ProductCardViewData } from '../../../features/ProductList/type';
import { FloatingButton_recent_product } from '../../components/cells/FloatingBox/FloatingButtons/FloatingButton_recent_product';
import { FloatingBox } from '../../components/cells/FloatingBox/FloatingBox';

class PhotoReviewList extends ListView<PhotoReviewCard> {
  override className = klass.photo_review_list;
  override ItemView = PhotoReviewCard;
}

export class PhotoReviewPage extends Page<{
  photo_reviews: PhotoReviewCardType[];
  layout_options: BaseLayoutOptions;
  recent_product?: ProductCardViewData;
  domain_name: string;
}> {
  override onMount() {
    super.onMount();
    SubHeaderMobile.handleChangeMenuOutside();
  }

  private photoReviewList = new PhotoReviewList(this.data.photo_reviews);
  protected override template(): Html {
    const { layout_options } = this.data;
    return html`
      <div
        class="${classes(klass.page, {
          [klass.all_crew]: this.isAllCrew,
        })}"
      >
        ${new LayoutView(
          {
            body: this.bodyHtml,
            footer_data: { ...footer_links, lang: ET.lang, is_mobile: layout_options.is_mobile },
          },
          {
            require_top_arrow_button: true,
            selected_bottom_tab_idx: -1,
            has_bottom_tab_bar: !layout_options.app_info,
            // full_width: true,
            floating_box: {
              recent_product: FloatingButton_recent_product.makeProps(this.data.recent_product),
              async renderHook(view: FloatingBox) {
                await FloatingBox.refreshRecentProductFloatingButton(view);
              },
            },
            ...layout_options,
          },
        )}
      </div>
    `;
  }

  @on(PaginationChangeEvent)
  async changePage(e: PaginationChangeEvent) {
    try {
      window.location.href = updateUrlParams(location.href, { page: String(e.detail.page) }, false);

      // CSR로 화면을 갱신하고 싶으면 아래 코드로..
      // const res = await PhotoReviewApiS.getPhotoReviews({ ...e.detail, limit: this.page_limit });
      // this.photoReviewList.set(res.data);
      // replaceUrl(updateUrlParams(location.href, { page: String(e.detail.page) }, false));
      // window.scrollTo(0, 0);
    } catch (e) {
      ConfigSentryF.error(e);
    }
  }

  private get bodyHtml() {
    const texts = {
      page_sub_title:
        this.isAllCrew && !this.isCreator
          ? ET('mps2::review::page_sub_title')
          : ET('mps2::review::page_sub_title__plus'),
      page_title: ET('mps2::review::page_title'),
    };

    return html`
      ${!this.isAllCrew || this.isMobile
        ? ''
        : new Breadcrumbs({
            steps: [
              { href: '/', label: ET('mps2::breadcrumbs::home') },
              { href: '/@/communities', label: texts.page_title },
            ],
          })}
      <div
        class="${classes(klass.body, {
          [klass.all_crew]: this.isAllCrew,
        })}"
      >
        ${new Title({
          title: texts.page_title,
          sub_title: texts.page_sub_title,
          size: this.data.layout_options.is_mobile ? 'large' : 'xLarge',
        })}
        ${this.data.photo_reviews.length ? this.photoReviewList : this.emptyHtml}
        <div class="${klass.pagination_wrap}">${new Pagination(this.initPageData)}</div>
      </div>
    `;
  }

  private get emptyHtml() {
    const empty_img_src =
      '//s3.marpple.co/files/u_2308668/2024/5/original/42fe1220b1fe6255a00c1e9d7cbcded7fab301c51.png';

    const texts = {
      empty_title: ET('mps2::review::empty_title'),
      empty_title_desc: html.preventEscape(ET('mps2::review::empty_title_desc')),
      button_text: T('order_list::마플샵 둘러보기'),
    };

    const link = `/${ET.lang}${this.isCreator ? `/${this.data.domain_name}` : ''}`;
    return html`
      <div class="${klass.empty}">
        <div class="${klass.img_wrap}">
          <img src="${empty_img_src}" alt="empty photo-review icon" />
        </div>
        <div class="${klass.wrap}">
          <div class="${klass.title} ${typo('20_bold')}">${texts.empty_title}</div>
          <div class="${klass.title_desc} ${typo('16_medium')}">${texts.empty_title_desc}</div>
        </div>
        <div class="buttons">
          <a
            ${this.isAllCrew
              ? html`data-post-message="${dataStr(
                  this.isCreator ? makeUtilScreenNavigate(link) : makeHomeScreenNavigate(),
                )}"`
              : ''}
            href="${link}"
          >
            ${new ButtonAction({
              label: texts.button_text,
              klass: klass.button,
              type: 'blue',
              size: 'large',
            })}
          </a>
        </div>
      </div>
    `;
  }

  private get initPageData() {
    const size = this.isMobile ? 5 : 10;
    return { ...rune.getSharedData(this)?.page_data, size };
  }

  private get isAllCrew() {
    return isAllCrewById(rune.getSharedData(this)?.crew_id || 1);
  }

  private get isCreator() {
    return rune.getSharedData(this)?.is_creator ?? false;
  }

  private get isMobile() {
    return rune.getSharedData(this)?.is_mobile;
  }
}
